import { useState, type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Star, BookOpen, Archive, ListTree } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchField } from "@/components/ui/search-field";
import { CountPill } from "../CountPill.js";

/** The active filter chip. `"all"` = the drill navigation; the rest are global flat lists (FN4a). */
export type FilterKey = "all" | "favourites" | "manual" | "archived";

export interface FacilityFilterCounts {
  /** Total facilities at the CURRENT level (active shown + archived) — drives the All chip + the "X of Y" denominator. */
  all: number;
  /** Active (non-archived) facilities shown at the current level — the "X of Y" numerator. */
  shown: number;
  /** GLOBAL favourite-facility count (realm-wide) — the Favourites list is not a level concept (fork #1). */
  favourites: number;
  /** GLOBAL favourite-MANUAL count (realm-wide, active + directly-viewable) — the "Manual favourites" flat list. */
  manual: number;
  /** GLOBAL archived-facility count (realm-wide). */
  archived: number;
}

/**
 * The facility-list header (FN4 — Mockup A): the "Facility selection" title + a text filter on the top row, and a row
 * of round filter chips (All · Favourites · Manual favourites · Archived) with live counts + an "X of Y" summary.
 * ("Add facility" lives at the END of the root tree, not here.)
 *
 * The active chip is **controlled** by {@link FacilityDrilldown} (FN4a) — it drives the main content: `all` = the drill
 * navigation; `favourites`/`archived` = global flat lists. Chips are round pills (selected = theme-inverting primary,
 * unselected = a visible muted fill), matching the app's chip pattern; **Manual favourites** is disabled until the
 * manuals-favourite UI exists. The free-text search box opens the global **search** view on Enter (FN4b — `onSearch`).
 */
export function FacilityFilterBar({
  counts,
  active,
  onActiveChange,
  onSearch,
  showArchived,
  trailing,
}: {
  counts: FacilityFilterCounts;
  active: FilterKey;
  onActiveChange: (key: FilterKey) => void;
  /** Submit (Enter) the search box → open the global search view (FN4b). */
  onSearch: (query: string) => void;
  /** Offer the Archived chip — only while editing facilities (owner 2026-11-02). */
  showArchived: boolean;
  /** Rendered right of the "X of Y" summary — the drill-down's "Bereiche editieren" toggle (redesign 2026-10-21). */
  trailing?: ReactNode;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  // The summary reflects the ACTIVE view: per-level "X of Y" for the drill, else the global flat-list count.
  //
  // `count` is i18next's reserved plural selector, so the `_one` / `_other` forms are chosen by the LOCALE's own
  // rules. This replaced two hand-written `n === 1 ? "facility" : "facilities"` helpers — correct for English and
  // wrong for everything else, since they also hardcoded the domain NOUN and so could never show Takeda its own
  // terminology. Each form now reads the noun through the glossary, which the tenant overlay redefines.
  const summary =
    active === "favourites"
      ? t("facilitySelection.summary.favourites", { count: counts.favourites })
      : active === "manual"
        ? t("facilitySelection.summary.manualFavourites", { count: counts.manual })
        : active === "archived"
          ? t("facilitySelection.summary.archived", { count: counts.archived })
          : t("facilitySelection.summary.level", { count: counts.all, shown: counts.shown });

  const chip = (key: FilterKey, label: string, Icon: ComponentType<{ className?: string }>, n: number | null, disabled = false) => {
    const selected = active === key;
    return (
      <Button
        variant={selected ? "default" : "outline"}
        className="h-9 rounded-full pr-1.5 pl-3 text-[13px] font-semibold max-sm:flex-auto max-sm:gap-1 max-sm:pl-2.5"
        aria-pressed={selected}
        isDisabled={disabled}
        onPress={() => onActiveChange(key)}
      >
        <Icon className="size-3.5" aria-hidden="true" />
        {label}
        {n != null ? <CountPill n={n} /> : null}
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Row 1 — title + text filter. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Same type as the facility name in the manuals view (ManualsViewShell's FacilityTitle) — one page-title style. */}
        <h1 id="facilities-heading" className="text-2xl font-bold tracking-tight">
          {t("facilitySelection.facilityHeader")}
        </h1>
        {/* Enter opens the global search view (FN4b) for the typed query — the box is a search launcher, not an inline filter. */}
        <SearchField
          label={t("facilitySelection.searchLabel")}
          placeholder={t("facilitySelection.searchPlaceholder")}
          value={query}
          onChange={setQuery}
          clearLabel={t("facilitySelection.clearSearch")}
          onSubmit={(q) => {
            if (q.trim()) onSearch(q);
          }}
          className="w-full sm:w-[300px]"
        />
      </div>

      {/* Row 2 — filter chips + the "X of Y" summary. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="group" aria-label={t("facilitySelection.filterGroup")} className="flex flex-wrap items-center gap-2 max-sm:w-full">
          {chip("all", t("facilitySelection.chip.all"), ListTree, counts.all)}
          {chip("favourites", t("facilitySelection.chip.favourites"), Star, counts.favourites)}
          {chip("manual", t("facilitySelection.chip.manualFavourites"), BookOpen, counts.manual)}
          {showArchived ? chip("archived", t("facilitySelection.chip.archived"), Archive, counts.archived) : null}
        </div>
        {/* Phones get a reduced view (owner 2026-11-02): no summary and no edit switch below `md`. */}
        <div className="flex flex-wrap items-center gap-4 max-md:hidden">
          <span className="text-muted-foreground shrink-0 text-[13px] tabular-nums">{summary}</span>
          {trailing}
        </div>
      </div>
    </div>
  );
}
